import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import dailyPauseOptionsImage from '../assets/daily-pause-options-image.svg';
import { useTranslation } from 'react-i18next';
import { captureException } from 'src/shared/helpers/captureException';

import {
    Button as ButtonBase,
    HeadingText40,
    BodyTextSmall,
    ComponentTextSmall,
    pxToRem,
} from '@modernhealth/vital-components';

import { filterNullish } from '@modernhealth/filter-nullish';
import { EventCalendarModal } from 'src/modules/scheduledContent/components/EventCalendarModal';
import { DailyPausePageWrapper } from '../components/DailyPausePageWrapper';
import { Redirect, useHistory } from 'react-router-dom';
import { MobileAppInformationModal } from 'src/shared/components/MobileAppInformationModal';
import { useDailyPauseReminderCalendarUrls } from '../hooks/useDailyPauseReminderCalendarUrls';
import { OptionRow } from '../components/OptionRow';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
    SharedPreferencesKeys,
    CleverTapProxyNotificationCategories,
} from 'src/modules/user/models/user';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'src/config/rootStore';
import { appClient } from 'src/config/apollo';
import { useUpdateAccountSharedPreferencesMutation } from 'src/modules/scheduledContent/documents/updateAccountSharedPreferencesMutation.generated';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import { useUpdateNotificationPreferencesMutation } from '../documents/updateNotificationPreferencesMutation.generated';
import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';
import { analyticsClient, EventType } from 'src/shared/services/analytics';
import { DailyPauseAdditionalOptionsTestId } from 'src/modules/scheduledContent/pages/testIds';

const updatedUserPreferences = {
    key: SharedPreferencesKeys.DailyPauseRemindersPageVisited,
    value: 'true',
};

export const DailyPauseAdditionalOptions: React.FC = () => {
    const { t } = useTranslation('dailyPause');
    const { notificationPreferences } = useSelector(getUserInformation);
    const [mobileAppModalVisible, setMobileAppModalVisible] = useState(false);
    const [calendarEventModalVisible, setCalendarEventModalVisible] = useState(false);
    const [confirmButtonDone, setConfirmButtonDone] = useState(false);
    const history = useHistory<{ dailyPauseRemindersPageVisited: boolean }>();
    const {
        releaseDiginShowDailyPauseCalendarRemindersWeb,
        releaseDiginShowDailyPauseNotificationsWeb,
    } = useFlags();
    const dailyPauseSettingsEnabled =
        releaseDiginShowDailyPauseCalendarRemindersWeb ||
        releaseDiginShowDailyPauseNotificationsWeb;

    const [updateAccountSharedPreferencesMutation, { error }] =
        useUpdateAccountSharedPreferencesMutation({
            client: appClient,
        });

    const dispatch = useDispatch<Dispatch>();

    const changeUserPreference = useCallback(async () => {
        await updateAccountSharedPreferencesMutation({
            variables: updatedUserPreferences,
        });

        if (error) {
            captureException(error);
        }
    }, [error, updateAccountSharedPreferencesMutation]);

    useEffect(() => {
        if (!history.location.state.dailyPauseRemindersPageVisited) {
            changeUserPreference();

            dispatch.user.updateSharedPreferences(updatedUserPreferences);
        }
    }, [
        changeUserPreference,
        dispatch.user,
        history.location.state.dailyPauseRemindersPageVisited,
    ]);

    const handleRedirect = () => {
        history.push('/pause/stats');
    };

    const { loading, handleReminderCreation } = useDailyPauseReminderCalendarUrls();
    const [updateNotificationPreferencesMutation, { loading: updatingNotificationPreferences }] =
        useUpdateNotificationPreferencesMutation();

    const options = filterNullish([
        releaseDiginShowDailyPauseNotificationsWeb
            ? {
                  title: 'dailyPause:additionalOptions.email_reminders.title',
                  description: 'dailyPause:additionalOptions.email_reminders.description',
                  onAction: async () => {
                      const newNotificationPreferences = {
                          ...notificationPreferences,
                          [CleverTapProxyNotificationCategories.DailyPauseRemindersEmail]:
                              !notificationPreferences.dailyPauseRemindersEmail,
                      };

                      dispatch.user.saveUserInfo({
                          notificationPreferences: newNotificationPreferences,
                      });

                      analyticsClient.track(
                          'Daily Pause: reminders email current setting',
                          EventType.API,
                          {
                              page_name: 'daily-pause-notifications-and-reminders-preferences',
                              daily_pause_reminders_email:
                                  !notificationPreferences.dailyPauseRemindersEmail,
                          },
                      );

                      setConfirmButtonDone(true);

                      try {
                          await updateNotificationPreferencesMutation({
                              variables: {
                                  notificationPreferences: newNotificationPreferences,
                              },
                          });
                      } catch (err) {
                          captureException(err);

                          toastNotificationsService.renderErrorToast(config.messages.genericError);

                          dispatch.user.saveUserInfo({
                              notificationPreferences: {
                                  ...notificationPreferences,
                                  [CleverTapProxyNotificationCategories.DailyPauseRemindersEmail]:
                                      notificationPreferences.dailyPauseRemindersEmail,
                              },
                          });
                      }
                  },
                  switchValue: notificationPreferences.dailyPauseRemindersEmail,
                  disabled: updatingNotificationPreferences,
              }
            : null,
        releaseDiginShowDailyPauseCalendarRemindersWeb
            ? {
                  title: 'dailyPause:additionalOptions.calendar_reminders.title',
                  description: 'dailyPause:additionalOptions.calendar_reminders.description',
                  onAction: () => {
                      analyticsClient.trackClick('Daily Pause: open add to calendar modal', {
                          page_name: 'daily-pause-notifications-and-reminders-preferences',
                      });
                      setCalendarEventModalVisible(true);
                  },
              }
            : null,
    ]);

    const confirmButtonLabel = confirmButtonDone
        ? 'dailyPause:celebration_modal.cta_done'
        : 'dailyPause:celebration_modal.cta_not_now';

    if (!dailyPauseSettingsEnabled) {
        return <Redirect to="/dashboard" />;
    }

    return (
        <DailyPausePageWrapper
            onRedirect={handleRedirect}
            testId={DailyPauseAdditionalOptionsTestId.Container}
        >
            <ContentWrapper>
                <DailyPauseOptionsImage src={dailyPauseOptionsImage} alt="" />
                <HeadingText40 marginBottom={26} centered>
                    {t('additionalOptions.headerText')}
                </HeadingText40>
                <OptionsSection>
                    {options.map(({ title, ...props }) => (
                        <OptionRow {...props} title={title} key={`notification_option_${title}`} />
                    ))}
                </OptionsSection>
                <Button
                    variant={confirmButtonDone ? 'primary' : 'secondary'}
                    title={t(confirmButtonLabel)}
                    onClick={handleRedirect}
                    size="large"
                    fluid
                >
                    {t(confirmButtonLabel)}
                </Button>
                <BodyTextSmall>
                    {t('dailyPause:additionalOptions.use_or_download')}{' '}
                    <Link onClick={() => setMobileAppModalVisible(true)}>
                        <ComponentTextSmall css={{ display: 'inline' }} fontColor="blue55">
                            {t('dailyPause:additionalOptions.mobile_app')}
                        </ComponentTextSmall>
                    </Link>
                </BodyTextSmall>
            </ContentWrapper>
            <MobileAppInformationModal
                onModalClose={() => setMobileAppModalVisible(false)}
                isVisible={mobileAppModalVisible}
            />
            <EventCalendarModal
                handleModalClose={() => setCalendarEventModalVisible(false)}
                isVisible={calendarEventModalVisible}
                loading={loading}
                onCalendarEventClick={calendarUrlType => {
                    if (calendarUrlType === 'googleCalendarUrl') {
                        analyticsClient.trackClick('Daily Pause: add to google calendar', {
                            page_name: 'daily-pause-notifications-and-reminders-preferences',
                        });
                    }

                    if (calendarUrlType === 'icsUrl') {
                        analyticsClient.trackClick('Daily Pause: add to local calendar (.ics)', {
                            page_name: 'daily-pause-notifications-and-reminders-preferences',
                        });
                    }

                    handleReminderCreation(calendarUrlType);
                }}
                callback={() => setConfirmButtonDone(true)}
            />
        </DailyPausePageWrapper>
    );
};

const ContentWrapper = styled.div({
    width: '100%',
    maxWidth: 500,
    margin: '0 auto',
});

const Button = styled(ButtonBase)({
    marginTop: pxToRem(36),
    marginBottom: pxToRem(20),
});

const DailyPauseOptionsImage = styled.img({
    display: 'block',
    width: pxToRem(250),
    margin: '0 auto',
    marginBottom: pxToRem(38),
});

const OptionsSection = styled.div({
    marginBottom: pxToRem(62),
});

const Link = styled('a')({
    cursor: 'pointer',
    textDecoration: 'underline',

    '&:hover': {
        textDecoration: 'underline',
    },
});
